//-- Copyright (c) 2021 TMT Insights Group, LLC
import React from 'react';
import styled from '@emotion/styled';
import TableStatus from '../_common/Table/TableStatus';
import Autocomplete, { AutocompleteProps } from '../_common/Form/Autocomplete';
import { updateStatus } from '../../redux/slices/dataTableSlice';
import { useToast } from '../_common/Toast';
import { selectJobHumanStatuses } from '../../redux/selectors/constantsSelectors';
import { selectCanLinkNewAsset } from '../../redux/selectors/authSelectors';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';

// only a non system generated status can be changed to a non system generated status
// sysgen statuses cannot be updated by a user

type StatusDropdownProps = {
  id: number;
  status: string;
  statusReason?: string;
};

const StatusDropdown = ({ id, status, statusReason }: StatusDropdownProps) => {
  const dispatch = useAppDispatch();
  const humanStatuses = useAppSelector(selectJobHumanStatuses);
  const canLinkNewAsset = useAppSelector(selectCanLinkNewAsset);
  const canEdit = Object.keys(humanStatuses).includes(status);
  const { success } = useToast();

  if (!canEdit || !canLinkNewAsset) return <TableStatus status={status} statusReason={statusReason} />;

  const options = Object.entries(humanStatuses).map((x) => ({ label: x[1], value: x[0] }));

  const handleChange: AutocompleteProps['onChange'] = (_, val) => {
    dispatch(updateStatus({ id, status: val.value }))
      .unwrap()
      .then(() => success('Status updated successfully!'));
  };

  return (
    <Wrapper>
      <StyledAutoComplete
        value={options.find((option) => option.value === status)}
        options={options}
        getOptionLabel={(option) => option.label}
        onChange={handleChange}
        onClick={(e) => e.stopPropagation()}
        size="small"
        disableClearable
        hiddenLabel
      />
      {statusReason ? (
        <TableStatus status={status} statusReason={statusReason} textOnly dotOnly />
      ) : null}
    </Wrapper>
  );
};

export default StatusDropdown;

const Wrapper = styled.div(
  () => `
  display: flex;
  align-items: center;
  column-gap: 4px;
`
);

const StyledAutoComplete = styled(Autocomplete)(
  ({ theme }) => `
  min-width: 160px;
  margin: ${theme.spacing(-1, 0)};
  & .MuiInputBase-input {
    font-weight: ${theme.typography.fontWeightMedium};
  }
`
);
